import { useState } from "react";
import Papa from "papaparse";
import { toast } from "react-toastify";
import { bulkAddParties } from "../../services/services";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";

const BulkUploadParty = () => {
  const [csvData, setCsvData] = useState([]); 
  const [showUploadInfo, setShowUploadInfo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [statusMap, setStatusMap] = useState({});
  const [submitted, setSubmitted] = useState(false);
  // CSV Headers
  const headers = [
    "name",
    "partyType",
    "email",
    "mobile",
    "gstin",
    "gstType",
    "billingAddress",
    "shippingAddress",
  ];

  // Download Sample CSV
  const downloadSampleCSV = () => {
    const sampleRow = [
      "ABC Traders",
      "vendor",
      "abc@gmail.com",
      "9876543210",
      "23ABCDE1234F1Z5",
      "Registered",
      "Bhopal MP",
      "Indore MP",
    ];

    const csvContent = headers.join(",") + "\n" + sampleRow.join(",");
    const blob = new Blob([csvContent], { type: "text/csv" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "sample_parties.csv";
    link.click();
  };

  // Drag & Drop
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload({ target: { files: [file] } });
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDragEnter = () => setIsDragging(true);
  const handleDragLeave = () => setIsDragging(false);

  // Upload CSV
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "text/csv") {
      toast.error("Only CSV files allowed!");
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const normalizedData = results.data.map((row) => ({
          name: row.name || "",
          partyType: row.partyType || "customer",
          email: row.email || "",
          mobile: row.mobile || "",
          gstin: row.gstin || "",
          gstType: row.gstType || "Unregistered",
          billingAddress: row.billingAddress || "",
          shippingAddress: row.shippingAddress || "",
        }));

        setCsvData(normalizedData);
        setShowUploadInfo(true);
        toast.success("CSV loaded successfully");
      },
    });
  };

  // Submit Data
  const handleSubmit = async () => {
    try {
      if (!csvData.length) {
        toast.error("No data to upload");
        return;
      }
      const formattedData = csvData.map((party) => ({
        ...party,
        partyType: party.partyType?.toLowerCase().trim(),
      }));

      const res = await bulkAddParties(formattedData);

      const skippedRows = res?.data?.skippedRows || [];

      const skippedMap = {};
      skippedRows.forEach((row) => {
        skippedMap[row.name] = row.reason;
      });

      setStatusMap(skippedMap);
      setSubmitted(true);
      toast.success(
        `${res.data.inserted} saved, ${res.data.skipped} already exist`,
      );
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    }
  };

  return (
    <div id="main" className="p-3">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 custom-title">Bulk Upload Parties</h4>

        <button className="btn btn-primary" onClick={downloadSampleCSV}>
          <i className="bi bi-download"></i>
          <span className="d-none d-md-inline ms-1">Sample CSV</span>
        </button>
      </div>

      {/* UPLOAD UI */}
      <div className="row g-4 mt-4 px-3">
        <div className="col-md-4">
          <label
            htmlFor="csvInput"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            className="border-dashed p-5 rounded text-center d-flex justify-content-center flex-column"
            style={{
              cursor: "pointer",
              background: isDragging ? "#E3F2FD" : "#F8F9FA",
              border: isDragging ? "2px dashed #0d6efd" : "2px dashed #b4b4b4",
              height: "20vh",
              transition: "0.2s",
            }}
          >
            <h5>
              <i className="bi bi-upload"></i> Upload CSV File
            </h5>
            <p className="text-muted">
              {isDragging ? "Drop file here" : "Click CSV file"}
            </p>
          </label>

          <input
            id="csvInput"
            type="file"
            accept=".csv"
            hidden
            onChange={handleFileUpload}
          />
        </div>
      </div>

      {/* INFO */}
      {showUploadInfo && (
        <div className="alert alert-warning mt-3">
          Download <b>Sample CSV file</b> and keep column headings unchanged
          while uploading.
        </div>
      )}

      {/* PREVIEW */}
      {csvData.length > 0 && (
        <div className="mt-4">
          <h5>Preview Parties ({csvData.length})</h5>

          <div className="table-responsive mt-3">
            <table className="table table-bordered text-nowrap">
              <thead className="table-dark">
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>GSTIN</th>
                  <th>GST Type</th>
                  <th>Billing Address</th>
                  <th>Shipping Address</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {csvData.map((party, index) => { 
                  const status = statusMap[party.name];
                  return (
                    <tr key={index}>
                      <td>{party.name}</td>
                      <td>{party.partyType}</td>
                      <td>{party.email}</td>
                      <td>{party.mobile}</td>
                      <td>{party.gstin}</td>
                      <td>{party.gstType}</td>
                      <td>{party.billingAddress}</td>
                      <td>{party.shippingAddress}</td>
                      <td>
                        {!submitted ? (
                          <span className="text-secondary fw-bold">
                            Pending
                          </span>
                        ) : status ? (
                          <span className="text-danger fw-bold">{status}</span>
                        ) : (
                          <span className="text-success fw-bold">Saved</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="d-flex justify-content-end mt-2">
              <button className="btn btn-primary" onClick={handleSubmit}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default headerLayoutWrapper(BulkUploadParty);
